import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  companyRelationshipApi,
  RelationshipVerification,
} from 'shared/services/companyRelationshipApi';
import {
  useSubmitRelationshipVerification,
  useReviewRelationshipVerification,
  useViewRelationshipDocument,
} from 'shared/hooks/mutations/useRelationshipVerificationMutations';
import { useReviewVerification, useViewDocument } from 'shared/hooks/mutations/useUserVerificationMutations';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { isHomeCompanyAdminOf } from 'shared/utils/roleUtils';

const STATUS_LABEL: Record<string, string> = {
  not_submitted: 'Sin enviar',
  pending: 'En revisión',
  in_review: 'En revisión',
  approved: 'Aprobado',
  rejected: 'Rechazado',
  expired: 'Vencido',
};

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
  not_submitted: 'default',
  pending: 'warning',
  in_review: 'warning',
  approved: 'success',
  rejected: 'error',
  expired: 'error',
};

interface Props {
  relationshipId: string;
  /** 'provider': la empresa proveedora sube documentos. 'client': la
   *  empresa cliente revisa (aprueba/rechaza) lo que el proveedor subió. */
  mode: 'provider' | 'client';
  /** El id de la empresa proveedora de esta relación — solo se usa en
   *  mode="client" para detectar si el usuario actual, aunque esté viendo
   *  esto como colaborador externo del cliente, es en realidad el
   *  company_admin de esa empresa proveedora (ver isHomeCompanyAdminOf) y
   *  por lo tanto debe poder subir documentos por su propia empresa sin
   *  tener que cambiar de empresa activa. */
  providerCompanyId?: string;
}

/**
 * Documentos requeridos por el cliente a nivel de relación — el
 * company_admin del proveedor los sube en representación de toda la
 * empresa (aún sin usuarios individuales asignados), el cliente los
 * revisa. Ver relationshipVerificationController en el backend.
 */
const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-CR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatFrequency = (row: RelationshipVerification): string => {
  if (!row.requiresRenewal || !row.validityPeriod) return 'Única vez';
  if (row.validityPeriod % 365 === 0) {
    const years = row.validityPeriod / 365;
    return `Cada ${years} año${years > 1 ? 's' : ''}`;
  }
  if (row.validityPeriod % 30 === 0) {
    const months = row.validityPeriod / 30;
    return `Cada ${months} mes${months > 1 ? 'es' : ''}`;
  }
  return `Cada ${row.validityPeriod} días`;
};

export const RelationshipVerificationsCard: React.FC<Props> = ({ relationshipId, mode, providerCompanyId }) => {
  const { hasRole, user } = useAuth();
  // A platform role or the CLIENT's own company_admin can submit on a
  // provider's behalf now (see
  // relationshipVerificationController.submitRelationshipVerification) —
  // surface the upload action even in mode="client" so they don't have to
  // switch into the provider's own account just to act for them. Safe to
  // check the bare role here (no relationship-specific scoping) because
  // this component is only ever reached already scoped to the viewer's own
  // company's relationships — the BE re-checks regardless.
  //
  // Also covers the provider's OWN company_admin, viewing this same
  // mode="client" card as an externally-lent colaborador of the client
  // (their client-side role is capped to client_staff there, so `hasRole`
  // alone reads false) — isHomeCompanyAdminOf looks at their home company
  // entry instead of the active one, matching what the BE already allows.
  const canActAsProvider =
    mode === 'provider' ||
    hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN]) ||
    isHomeCompanyAdminOf(user, providerCompanyId);
  // Mirrors the backend's isClientAdminOf (relationshipVerificationController.ts)
  // — Aprobar/Rechazar are a client-side action reserved for the client's
  // company_admin and platform admins, never a provider or client_approver one.
  const canReview =
    mode === 'client' &&
    hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.COMPANY_ADMIN]);
  const [rows, setRows] = useState<RelationshipVerification[]>([]);
  const [isCompliant, setIsCompliant] = useState(true);
  const [blockUnverifiedAccess, setBlockUnverifiedAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [rejectDialog, setRejectDialog] = useState<{ row: RelationshipVerification } | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pendingUploadRuleId = useRef<string | null>(null);

  const submitVerification = useSubmitRelationshipVerification();
  // 'individual' rows (verificationLevel!=='company') hit the existing
  // per-user review/download endpoints — reviewVerification/viewDocument.
  // 'company' rows are genuinely separate submissions (CompanyVerificationSubmission,
  // no `user` field at all) and go through the new relationship-scoped hooks.
  const reviewVerification = useReviewVerification();
  const viewDocument = useViewDocument();
  const reviewRelationshipVerification = useReviewRelationshipVerification();
  const viewRelationshipDocument = useViewRelationshipDocument();

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await companyRelationshipApi.getVerifications(relationshipId);
      setRows(data.items);
      setIsCompliant(data.isCompliant);
      setBlockUnverifiedAccess(data.blockUnverifiedAccess);
    } catch (err: any) {
      setError(err?.message || 'Error al cargar las verificaciones requeridas');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [relationshipId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  if (!loading && rows.length === 0 && !error) return null;

  const triggerUpload = (companyVerificationId: string) => {
    pendingUploadRuleId.current = companyVerificationId;
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const ruleId = pendingUploadRuleId.current;
    e.target.value = '';
    if (!file || !ruleId) return;
    setUploadingId(ruleId);
    setError(null);
    try {
      await submitVerification.mutateAsync({ relationshipId, companyVerificationId: ruleId, file });
      await fetchRows();
    } catch (err: any) {
      setError(err?.message || 'Error al subir el documento');
    } finally {
      setUploadingId(null);
    }
  };

  const handleView = async (row: RelationshipVerification) => {
    try {
      const doc =
        row.verificationLevel === 'company'
          ? await viewRelationshipDocument.mutateAsync({
              relationshipId,
              companyVerificationId: row.companyVerificationId,
            })
          : await viewDocument.mutateAsync({
              verificationType: 'company',
              userVerificationId: row.submissionId!,
            });
      window.open(doc.url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setError(err?.message || 'Error al abrir el documento');
    }
  };

  const handleApprove = async (row: RelationshipVerification) => {
    try {
      if (row.verificationLevel === 'company') {
        await reviewRelationshipVerification.mutateAsync({
          relationshipId,
          companyVerificationId: row.companyVerificationId,
          action: 'approve',
        });
      } else {
        await reviewVerification.mutateAsync({
          verificationType: 'company',
          userVerificationId: row.submissionId!,
          action: 'approve',
        });
      }
      await fetchRows();
    } catch (err: any) {
      setError(err?.message || 'Error al aprobar');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectDialog || !rejectionReason.trim()) return;
    const { row } = rejectDialog;
    try {
      if (row.verificationLevel === 'company') {
        await reviewRelationshipVerification.mutateAsync({
          relationshipId,
          companyVerificationId: row.companyVerificationId,
          action: 'reject',
          rejectionReason: rejectionReason.trim(),
        });
      } else {
        await reviewVerification.mutateAsync({
          verificationType: 'company',
          userVerificationId: row.submissionId!,
          action: 'reject',
          rejectionReason: rejectionReason.trim(),
        });
      }
      setRejectDialog(null);
      setRejectionReason('');
      await fetchRows();
    } catch (err: any) {
      setError(err?.message || 'Error al rechazar');
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelected}
        accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
        style={{ display: 'none' }}
      />
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Documentos requeridos por el cliente
      </Typography>
      {error && <Alert severity="warning" sx={{ mb: 1.5 }}>{error}</Alert>}
      {!loading && blockUnverifiedAccess && !isCompliant && (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {mode === 'client'
            ? 'El acceso de usuarios externos de este proveedor está bloqueado hasta que se completen estas verificaciones.'
            : 'Su empresa tiene el acceso bloqueado para usuarios externos hasta completar estos documentos. Solo el administrador de la empresa puede ingresar mientras tanto.'}
        </Alert>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Documento</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Frecuencia</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Vence</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.map((row) => (
              <TableRow key={row.companyVerificationId} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography variant="body2" fontWeight={600}>{row.name}</Typography>
                    {row.verificationLevel === 'company' && (
                      <Chip label="Empresa" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                    )}
                  </Box>
                  {row.status === 'rejected' && row.rejectionReason && (
                    <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
                      {row.rejectionReason}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={STATUS_LABEL[row.status] || row.status}
                    color={STATUS_COLOR[row.status] || 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{formatFrequency(row)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color={row.status === 'expired' ? 'error.main' : 'text.secondary'}>
                    {formatDate(row.expiryDate)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    {row.submissionId && (
                      <Tooltip title="Ver documento">
                        <IconButton size="small" onClick={() => handleView(row)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Reenvío/reemplazo — disponible en cualquier estado,
                        incluyendo mientras está pending/in_review (el
                        proveedor puede corregir un envío antes de que el
                        reviewer llegue a verlo) o ya aprobado (corrige sin
                        esperar a que un reviewer lo rechace primero). El
                        envío vuelve a 'pending' en cualquier caso (ver
                        submitRelationshipVerification). */}
                    {canActAsProvider && (
                      <Tooltip title={row.status === 'pending' || row.status === 'in_review' ? 'Reemplazar documento enviado' : row.status === 'approved' ? 'Reemplazar documento' : row.status === 'rejected' ? 'Volver a enviar' : 'Subir documento'}>
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={uploadingId === row.companyVerificationId}
                            onClick={() => triggerUpload(row.companyVerificationId)}
                          >
                            {uploadingId === row.companyVerificationId
                              ? <CircularProgress size={16} />
                              : <UploadFileIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {/* Aprobar/Rechazar ya no se limitan a pending/in_review —
                        un company_admin/admin puede revertir una aprobación
                        (Rechazar sobre 'approved' regresa a 'pending', no a
                        'rejected' — ver reviewRelationshipVerification) o
                        volver a aprobar algo rechazado/vencido. Se oculta el
                        botón cuya acción sería un no-op en el estado actual. */}
                    {canReview && row.submissionId && row.status !== 'not_submitted' && (
                      <>
                        {row.status !== 'approved' && (
                          <Tooltip title="Aprobar">
                            <IconButton size="small" color="success" onClick={() => handleApprove(row)}>
                              <CheckIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {row.status !== 'rejected' && (
                          <Tooltip title={row.status === 'approved' ? 'Enviar a revisión nuevamente' : 'Rechazar'}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setRejectDialog({ row })}
                            >
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {rejectDialog?.row.status === 'approved'
            ? `Enviar "${rejectDialog?.row.name}" a revisión nuevamente`
            : `Rechazar "${rejectDialog?.row.name}"`}
        </DialogTitle>
        <DialogContent>
          {rejectDialog?.row.status === 'approved' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Este documento ya estaba aprobado. Al continuar volverá a estado
              "En revisión" — no queda marcado como rechazado.
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Motivo"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(null)}>Cancelar</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim() || reviewVerification.isPending}
            onClick={handleRejectSubmit}
          >
            {rejectDialog?.row.status === 'approved' ? 'Enviar a revisión' : 'Rechazar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RelationshipVerificationsCard;
